import { Component, Input, Output, EventEmitter, computed, inject, signal, ChangeDetectionStrategy } from '@angular/core';
import { Router, RouterLink } from '@angular/router';
import { TranslatePipe, TranslateService } from '@ngx-translate/core';
import { toSignal } from '@angular/core/rxjs-interop';
import { catchError, tap } from 'rxjs/operators';
import { of } from 'rxjs';
import {
  PreventionCatalogService,
  MarketCatalogFamily,
  MarketCatalogReferenceOffer,
} from '../../core/services/prevention-catalog.service';
import { PreventionOffersService, OrganizationOfferSummary } from '../../core/services/prevention-offers.service';
import { ToastService } from '../../core/services/toast.service';
import { PreventionGroupHeaderComponent } from '../../component/prevention-group-header/prevention-group-header.component';

interface ReferenceOfferFamilyGroup {
  family: MarketCatalogFamily;
  offers: MarketCatalogReferenceOffer[];
}

/**
 * Bibliothèque Waitora (Lot 9.0-E.E.E) -- catalogue de ReferenceOffer groupées par CatalogFamily,
 * même idiome que {@code PreventionBilanCatalogComponent} (lecture seule, recherche client-side,
 * aucune pagination -- 18 templates). "Ajouter à mes offres" ne crée RIEN en base : simple
 * navigation vers `/prevention-offres/nouvelle?fromTemplate=<code>` (section 10 du lot), le
 * formulaire existant reste le SEUL point d'écriture d'une Offer.
 */
@Component({
  selector: 'app-prevention-offer-library',
  standalone: true,
  imports: [TranslatePipe, RouterLink, PreventionGroupHeaderComponent],
  templateUrl: './prevention-offer-library.component.html',
  changeDetection: ChangeDetectionStrategy.Eager,
  styleUrls: ['./prevention-offer-library.component.scss'],
})
export class PreventionOfferLibraryComponent {
  /** `true` quand incrusté dans une sous-page Site (ex. "Ajouter une offre" -> "Bibliothèque
   * Waitora") -- masque le fil d'Ariane propre à cette page (l'hôte a déjà le sien) et remplace
   * la navigation réelle par les `@Output()` ci-dessous. */
  @Input() embedded = false;
  @Output() back = new EventEmitter<void>();
  @Output() addOffer = new EventEmitter<string>();
  /** Émis après un ajout direct réussi (section ci-dessous) -- l'hôte Site en profite pour rafraîchir
   * son propre catalogue "Offres du site", qui liste TOUTES les offres du groupe (actives ou non). */
  @Output() offerAdded = new EventEmitter<void>();
  /** Émis quand l'utilisateur veut activer, pour LE SITE COURANT, une Offer qui existe déjà dans le
   * catalogue du groupe (créée depuis un autre site) -- l'hôte Site ferme cette sous-page et ouvre
   * l'édition de cette ligne dans "Offres du site" (jamais un nouveau POST /offers, section ci-dessous). */
  @Output() activateForCurrentSite = new EventEmitter<string>();
  /** offerId des Offer déjà ACTIVES pour le site depuis lequel cette bibliothèque a été ouverte
   * (`embedded` uniquement -- toujours `null` en page autonome, pas de "site courant" là-bas). Le
   * catalogue du groupe reste partagé (voir `existingOfferIdByCode`) : ceci ne filtre JAMAIS la
   * liste, seulement l'action proposée par ligne (déjà proposée par ce site vs. à y ajouter). */
  @Input() activeOfferIdsForCurrentSite: ReadonlySet<string> | null = null;

  private readonly catalogSvc = inject(PreventionCatalogService);
  private readonly offersSvc = inject(PreventionOffersService);
  private readonly toast = inject(ToastService);
  private readonly translate = inject(TranslateService);
  private readonly router = inject(Router);

  readonly search = signal('');
  readonly loadError = signal(false);

  private readonly families = toSignal(
    this.catalogSvc.getFamilies().pipe(
      tap(() => this.loadError.set(false)),
      catchError(() => { this.loadError.set(true); return of(null); })
    ),
    { initialValue: null as MarketCatalogFamily[] | null }
  );

  private readonly referenceOffers = toSignal(
    this.catalogSvc.getReferenceOffers().pipe(
      catchError(() => { this.loadError.set(true); return of(null); })
    ),
    { initialValue: null as MarketCatalogReferenceOffer[] | null }
  );

  /** Codes `ReferenceOffer` déjà transformés en Offer du groupe (peu importe leur activation par
   * site -- une Offer déjà créée à partir d'un modèle apparaît, active ou non, dans TOUS les
   * catalogues de site, section [[prevention-offer-library]]). Jamais un simple prérequis local :
   * sans ce GET org-wide, "Ajouter à mes offres" recréerait un doublon à chaque nouvelle visite de
   * la page (bug constaté -- 3 "Bilan cardio-rénal" créés en testant), y compris via le formulaire
   * "Voir l'offre" qui n'a lui-même aucun garde-fou anti-doublon. */
  // Fix #2 (revue technique 2026-09-03) : ce GET protège contre la recréation d'un doublon (voir le
  // commentaire ci-dessus) -- un échec silencieux ici (ancien `catchError(() => of(null))` sans
  // `loadError`) faisait retomber `existingReferenceCodes` sur un Set VIDE, laissant "Ajouter à mes
  // offres" recréer exactement le doublon que ce GET est censé empêcher. Bloque désormais la page
  // comme `families`/`referenceOffers` en cas d'échec réseau, jamais un état "tout est ajoutable" par
  // défaut. Filet de sécurité serveur complémentaire : index unique partiel V230 côté backend.
  private readonly existingOffers = toSignal(
    this.offersSvc.getOffers().pipe(catchError(() => { this.loadError.set(true); return of(null); })),
    { initialValue: null as OrganizationOfferSummary[] | null }
  );
  /** referenceOfferCode -> offerId -- permet de rebondir vers l'activation par site d'une Offer déjà
   * créée (jamais une recréation : l'organisation ne peut avoir qu'UNE Offer par référence). */
  private readonly existingOfferIdByCode = computed<Map<string, string>>(() => {
    const map = new Map<string, string>();
    for (const o of this.existingOffers() ?? []) {
      if (o.referenceOfferCode) map.set(o.referenceOfferCode, o.offerId);
    }
    return map;
  });
  private readonly existingReferenceCodes = computed<Set<string>>(() => new Set(this.existingOfferIdByCode().keys()));

  readonly loading = computed(() => !this.loadError()
    && (this.families() === null || this.referenceOffers() === null || this.existingOffers() === null));

  /** Groupé par famille (position croissante), familles sans ReferenceOffer masquées (section 8 du lot). */
  private readonly groupedByFamily = computed<ReferenceOfferFamilyGroup[]>(() => {
    const families = this.families() ?? [];
    const offers = this.referenceOffers() ?? [];
    return families
      .slice()
      .sort((a, b) => a.position - b.position)
      .map(family => ({ family, offers: offers.filter(o => o.familyCode === family.code) }))
      .filter(group => group.offers.length > 0);
  });

  /**
   * Recherche insensible à la casse sur ReferenceOffer.name / description / CatalogFamily.label /
   * bilan.name (section 9 du lot) -- même limitation assumée que le catalogue des bilans (pas de
   * normalisation des accents, disproportionné pour 18 templates).
   */
  readonly filteredGroups = computed<ReferenceOfferFamilyGroup[]>(() => {
    const query = this.search().trim().toLowerCase();
    return this.groupedByFamily()
      .map(group => ({
        family: group.family,
        offers: !query ? group.offers : group.offers.filter(offer => this.matches(offer, group.family, query)),
      }))
      .filter(group => group.offers.length > 0);
  });

  /** URL de la vignette du modèle, ou `null` si ce ReferenceOffer n'a pas de photo par défaut. */
  photoUrl(offer: MarketCatalogReferenceOffer): string | null {
    return offer.hasPhoto ? this.catalogSvc.getReferenceOfferPhotoUrl(offer.code, offer.photoUpdatedAt) : null;
  }

  private matches(offer: MarketCatalogReferenceOffer, family: MarketCatalogFamily, query: string): boolean {
    if (offer.name.toLowerCase().includes(query)) return true;
    if (offer.description?.toLowerCase().includes(query)) return true;
    if (family.label.toLowerCase().includes(query)) return true;
    return offer.bilans.some(bilan => bilan.name.toLowerCase().includes(query));
  }

  /** Vide au sens global (bibliothèque vraiment vide) -- distinct d'une recherche sans résultat. */
  readonly isGloballyEmpty = computed(() => !this.loading() && !this.loadError() && (this.referenceOffers()?.length ?? 0) === 0);

  readonly hasSearchNoResults = computed(() =>
    !this.loading() && !this.loadError() && !this.isGloballyEmpty() &&
    this.search().trim().length > 0 && this.filteredGroups().length === 0
  );

  onSearchChanged(value: string): void {
    this.search.set(value);
  }

  /** Aucun appel POST ici -- ouvre juste le formulaire pré-rempli en lecture/édition avant tout
   * engagement (bouton "Voir l'offre"), même destination que l'ancien bouton unique. */
  viewOffer(code: string): void {
    if (this.embedded) { this.addOffer.emit(code); return; }
    this.router.navigate(['/prevention-offres/nouvelle'], { queryParams: { fromTemplate: code } });
  }

  /** Codes ajoutés PENDANT cette session (avec leur offerId), fusionnés avec `existingOfferIdByCode`
   * dans {@link isAdded}/{@link offerIdFor} -- affiche la puce verte immédiatement après un ajout
   * direct sans attendre que le GET org-wide ci-dessus se refasse. */
  private readonly addedOfferIdByCode = signal<Map<string, string>>(new Map());
  readonly addingCode = signal<string | null>(null);

  isAdded(code: string): boolean {
    return this.addedOfferIdByCode().has(code) || this.existingReferenceCodes().has(code);
  }

  /** offerId de l'Offer déjà créée pour ce modèle (session courante ou catalogue du groupe), ou
   * `null` si `!isAdded(code)`. */
  offerIdFor(code: string): string | null {
    return this.addedOfferIdByCode().get(code) ?? this.existingOfferIdByCode().get(code) ?? null;
  }

  /** `true` si l'Offer existe ET est déjà active pour le site courant (`embedded` uniquement) --
   * distingue "déjà proposée par CE site" (rien à faire) de "existe dans le catalogue du groupe,
   * pas encore ajoutée par ce site" (bouton d'activation, voir template). */
  isActiveOnCurrentSite(code: string): boolean {
    const offerId = this.offerIdFor(code);
    return !!offerId && (this.activeOfferIdsForCurrentSite?.has(offerId) ?? false);
  }

  /** Le catalogue du groupe est partagé entre sites (une Offer par référence, jamais dupliquée) --
   * mais chaque site l'active lui-même : quand l'offre existe déjà (créée depuis un autre site) mais
   * pas encore pour LE SITE COURANT, ce bouton ne rappelle jamais POST /offers, il amène directement
   * à l'activer pour ce site (uniquement affiché en mode `embedded`, voir template). */
  onConfigureClick(code: string): void {
    const offerId = this.offerIdFor(code);
    if (offerId) this.activateForCurrentSite.emit(offerId);
  }

  /** Ajout direct, SANS passer par le formulaire (bouton "Ajouter à mes offres") -- crée l'Offer avec
   * la composition par défaut du modèle. Reste un POST /offers ordinaire (même endpoint que le
   * formulaire), jamais une écriture bis : la ligne passe juste directement à l'état "ajoutée". */
  addDirectly(offer: MarketCatalogReferenceOffer): void {
    if (this.addingCode() || this.isAdded(offer.code)) return;
    this.addingCode.set(offer.code);
    this.offersSvc.createOffer({
      name: offer.name,
      description: offer.description,
      referenceOfferCode: offer.code,
      bilanCodes: offer.bilans.map(b => b.code),
      additionalItems: offer.additionalItems.map(a => a.label),
      // Contenu patient (lot "UI admin des offres prévention") : jamais sourcé depuis le
      // ReferenceOffer (la Bibliothèque Waitora n'en porte pas) -- le laboratoire les renseigne
      // ensuite via le formulaire d'édition, comme pour toute Offer créée depuis un template.
      patientDescription: null,
      patientProcedure: null,
      accessMode: null,
      fundingType: null,
      // Même défaut que OfferEntity côté backend -- la Bibliothèque Waitora ne porte aucune
      // préférence de faisabilité, le laboratoire l'ajuste ensuite via le formulaire d'édition.
      defaultImmediateFeasibility: true,
      // Jamais hérité du ReferenceOffer (même règle que le backend) -- activable ensuite via le
      // formulaire d'édition si la composition couvre réellement plusieurs domaines.
      allowMultiCategory: false,
    }).subscribe({
      next: (created) => {
        this.addingCode.set(null);
        this.addedOfferIdByCode.update(map => new Map(map).set(offer.code, created.offerId));
        this.toast.show(
          this.translate.instant('preventionOffres.library.addedToast'),
          'success',
          3000,
          this.translate.instant('preventionOffres.library.addedToastSubtitle', { name: offer.name })
        );
        this.offerAdded.emit();
      },
      error: () => {
        this.addingCode.set(null);
        this.toast.show(
          this.translate.instant('preventionOffres.library.addErrorToast'),
          'error',
          3000,
          this.translate.instant('preventionOffres.library.addErrorToastSubtitle')
        );
      },
    });
  }

  /** Destination déterministe (jamais window.history.back()), même point d'entrée que le CTA "Bibliothèque" de Mes Offres. */
  goBack(): void {
    if (this.embedded) { this.back.emit(); return; }
    this.router.navigate(['/prevention-offres']);
  }
}
